'use client';

import React, { useMemo } from 'react';
import styles from './BuySectionInfo.module.css';
import { H2 } from './styleConstants';
import { useSearchParams } from 'next/navigation';
import { useCartStore } from 'components/shared/Cart';
import {
  OffersCarousel,
  OffersDataType,
} from 'components/ring-air/OffersCarousel';
import { Currency } from 'utils/Currency';
import { useFormatPrice, CurrencySymbol } from 'hooks/pricing/useFormattedPrice';
import { HomePriceStriked } from 'content/products/uhHome';
import {
  ShopifyStore,
  getEffectiveStoreForDiscount,
} from 'lib/commerce/shopify/shopifyHelper';
import { useHomeContext } from '../HomeContext';
import { ShippingBox } from 'assets/icons';
import { FSAHSAComponent } from 'components/index';
import Image from 'next/image';
import BirthdayIcon from '../../../assets/birthday-icon.svg';

interface AffiliateDetails {
  image?: string;
  discount_configs: {
    [key in ShopifyStore]: {
      RING: {
        code: string;
      };
    };
  };
}

interface ReferralInfo {
  discount: number;
  discountCode: string;
}

interface BuySectionInfoProps {
  title: string;
  defaultCurrency: Currency;
  price: number;
  defaultStrikedPrice?: boolean;
  store: ShopifyStore;
  shippingEstimate?: React.ReactNode;
  affiliateDiscount?: number;
  affiliateDetails?: AffiliateDetails;
  referral?: ReferralInfo;
  defaultOfferText?: string;
  defaultOfferDiscount?: string | number;
  secondaryOfferData?: OffersDataType[];
}

const BuySectionContainer = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => (
  <div
    className={
      className
        ? `${styles.buySectionContainer} ${className}`
        : styles.buySectionContainer
    }
  >
    {children}
  </div>
);

const PriceContainer = ({
  className,
  children,
}: {
  isAffiliate: boolean;
  className?: string;
  children: React.ReactNode;
}) => (
  <p
    className={
      className ? `${styles.priceContainer} ${className}` : styles.priceContainer
    }
  >
    {children}
  </p>
);

const OfferFlexContainer = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => (
  <div
    className={
      className
        ? `${styles.offerFlexContainer} ${className}`
        : styles.offerFlexContainer
    }
  >
    {children}
  </div>
);

const OfferTag = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => (
  <p className={className ? `${styles.offerTag} ${className}` : styles.offerTag}>
    {children}
  </p>
);

export const BuySectionInfo: React.FC<BuySectionInfoProps> = ({
  title,
  defaultCurrency,
  price,
  defaultStrikedPrice,
  store,
  shippingEstimate,
  referral,
  defaultOfferText,
  secondaryOfferData,
}) => {
  const searchParams = useSearchParams();

  // Get campaign data from CartContext
  const getCampaignForProduct = useCartStore((s) => s.getCampaignForProduct);
  const homeCampaign = getCampaignForProduct('home');

  // Get values from HomeContext
  const { affiliateDiscount, affiliateDetails, effectiveDiscount } =
    useHomeContext();

  // Get currency symbol safely (same as Ring buy section)
  const currencySymbol =
    CurrencySymbol[defaultCurrency.toLowerCase() as Currency];

  // Check if we have a discount
  const hasDiscount = effectiveDiscount.amount > 0;

  // Use useMemo to cache the affiliate code calculation
  const affiliateCode = useMemo(() => {
    if (!affiliateDetails) return '';
    // Get effective store (matches the logic in HomeContext)
    const effectiveStore = getEffectiveStoreForDiscount(store);

    const config = affiliateDetails.discount_configs?.[effectiveStore];
    if (config?.HOME?.code) {
      return config.HOME.code;
    }

    // Fallback for direct code access
    return affiliateDetails.code || '';
  }, [affiliateDetails, store]);
  const formatPriceWithComma = useFormatPrice();

  const showOfferContainer = (() => {
    if (
      affiliateDetails ||
      referral ||
      defaultOfferText ||
      effectiveDiscount.amount > 0
    ) {
      return true;
    }
    return false;
  })();

  return (
    <BuySectionContainer className="buy-section title-info-content">
      {/* mobile section */}
      <div className="title-container">
        <FSAHSAComponent />
        <H2>{title}</H2>
        <p>A quiet yet powerful sleep monitor to help you rest better.</p>
      </div>

      {/* Price listing */}
      <PriceContainer
        className={`price ${hasDiscount ? 'affiliate' : ''}`}
        isAffiliate={hasDiscount}
      >
        {/* Always Striked prices */}
        {defaultStrikedPrice &&
        !hasDiscount &&
        HomePriceStriked[store as ShopifyStore] > price ? (
          <span className="strike-through">
            <>
              {currencySymbol}
              {formatPriceWithComma(HomePriceStriked[store as ShopifyStore])}
            </>
          </span>
        ) : null}

        {/* Regular price - with strikethrough if discounted */}
        <span
          className={hasDiscount ? 'strike-through' : ''}
          data-testid={hasDiscount ? undefined : 'buy-headline-price'}
        >
          <>
            {currencySymbol}
            {formatPriceWithComma(price)}
          </>
        </span>

        {/* Discounted price - only show if there's a discount */}
        {hasDiscount ? (
          <span data-testid="buy-headline-price">
            <>
              {currencySymbol}
              {formatPriceWithComma(price - effectiveDiscount.amount)}
            </>
          </span>
        ) : null}

        {false && <span className="early-bird-chip">Early Bird Price</span>}
      </PriceContainer>
      {effectiveDiscount.type === 'homePromoDiscount' ? (
        <p className="offerTag transparent">
          <span>{homeCampaign?.title || 'Special'} Offer applied</span>
        </p>
      ) : null}

      {showOfferContainer ? (
        <OfferFlexContainer className="offerFlex">
          {/* Birthday discount tag */}
          {effectiveDiscount.type === 'birthday' &&
          searchParams?.get('affiliateCode') === 'birthday' ? (
            <OfferTag className="offerTag">
              <Image
                src={BirthdayIcon}
                alt="birthday_icon"
                style={{
                  borderRadius: '0',
                }}
              />
              <span>
                {/* <span className="offerApplied">
                 Birthday
                </span>{' '} */}
                Birthday Discount Applied
                {/* {(() => {
                    const discountCode = router.query.discount as string;
                    const percentage = discountCode ? parseInt(discountCode.slice(-2)) || 15 : 15;
                    return `(${percentage}% off)`;
                  })()} */}
              </span>
            </OfferTag>
          ) : null}

          {/* Affiliate discount tag */}
          {effectiveDiscount.type === 'affiliate' &&
          affiliateDiscount > 0 &&
          affiliateDetails ? (
            <OfferTag className="offerTag">
              {affiliateDetails.image ? (
                <img src={affiliateDetails.image} alt="avatar" />
              ) : null}
              <span>
                <span className="offerApplied">
                  {affiliateCode}{' '}
                  {searchParams?.get('workAffiliate') ? 'ultrahuman' : ''}
                </span>{' '}
                Offer Applied
              </span>
            </OfferTag>
          ) : null}

          {/* Referral tag */}
          {effectiveDiscount.type === 'referral' && referral ? (
            <OfferTag className="offerTag">
              <span>
                <span className="offerApplied">{referral.discountCode}</span>{' '}
                Offer Applied
              </span>
            </OfferTag>
          ) : null}

          {/* Default offer tag - only show if no other offers */}
          {defaultOfferText &&
          !affiliateDetails &&
          !referral &&
          effectiveDiscount.amount === 0 ? (
            <OfferTag className="offerTag">
              <span>
                <span className="offerApplied">{defaultOfferText}</span> Offer
                Applied
              </span>
            </OfferTag>
          ) : null}
        </OfferFlexContainer>
      ) : null}

      <p className="shipping-estimate">
        {shippingEstimate ? (
          <>
            <ShippingBox /> {shippingEstimate}
          </>
        ) : (
          <></>
        )}
      </p>

      {secondaryOfferData && (
        <OffersCarousel data={secondaryOfferData} leftAligned={true} />
      )}
      {/* Overview */}
    </BuySectionContainer>
  );
};
